'use client'

import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import { Button } from '@/shared/ui/button/button'
import { Check, Calendar, Building2, TrendingUp } from 'lucide-react'
import { Company } from '@/shared/types/company'
import { cn } from '@/shared/lib/utils'
import { getDisplayTaxSystem } from '@/shared/lib/tax-system'

interface CompanyCardProps {
  company: Company
  onDetailsClick: (company: Company) => void
  onQuickView?: (company: Company) => void
  className?: string
}

export const CompanyCard = ({
  company,
  onDetailsClick,
  onQuickView,
  className
}: CompanyCardProps) => {
  const t = useTranslations('companies.card')
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0
    }).format(price)
  }

  const formatAmount = (amount: number) => {
    return `${amount} млн`
  }

  const getLatestTurnover = () => {
    const sorted = company.turnover.sort((a, b) => b.year - a.year)
    return sorted.slice(0, 4)
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      whileHover={{ y: -4, zIndex: 10 }}
      transition={{ duration: 0.3 }}
      className={cn(
        'bg-card border rounded-xl p-4 md:p-6 hover:border-orange-500 transition-all duration-300 hover:shadow-lg min-h-[420px] flex flex-col relative hover:z-10',
        className
      )}
    >
      {/* Заголовок */}
      <div className="flex items-start justify-between mb-4">
        <div className="min-w-0 flex-1 pr-2">
          <h3 className="text-xl font-bold text-foreground mb-1 truncate" title={company.name}>
            {company.name}
          </h3>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 px-2 py-1 rounded-md font-medium">
              {getDisplayTaxSystem(company.taxSystem, company.usnRate)}
            </span>
            <Calendar className="h-4 w-4" />
            <span>{company.registrationDate}</span>
          </div>
        </div>
      </div>

      {/* Банковские счета */}
      <div className="mb-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
          <Building2 className="h-4 w-4" />
          <span>Банковские счета:</span>
        </div>
        <p className="text-sm truncate">{company.bankAccounts.join(', ')}</p>
      </div>

      {/* ОКВЭД */}
      <div className="mb-4 h-10">
        <p
          className="text-sm font-medium text-foreground leading-5 overflow-hidden"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {company.okvedCode} {company.okvedDescription}
        </p>
      </div>

      {/* Обороты */}
      <div className="mb-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
          <TrendingUp className="h-4 w-4" />
          <span>Обороты</span>
        </div>
        <div className="space-y-1 h-20">
          {getLatestTurnover().map((item) => (
            <div key={item.year} className="flex justify-between text-sm">
              <span className="text-muted-foreground">{item.year} -</span>
              <span className="font-medium">{formatAmount(item.amount)}</span>
            </div>
          ))}
          {/* Заполняем пустые строки до 4 */}
          {Array.from({ length: Math.max(0, 4 - getLatestTurnover().length) }).map((_, index) => (
            <div key={`empty-${index}`} className="h-5"></div>
          ))}
        </div>
      </div>

      {/* Уставный капитал */}
      <div className="mb-4 h-5">{/* Уставный капитал скрыт */}</div>

      {/* Верификация */}
      <div className="mb-4 h-12">
        {company.isVerified && (
          <div className="flex items-start gap-2 text-sm text-green-600 dark:text-green-400">
            <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="leading-4">
              В черных списках ЦБ, ЗСК, 115 и 764-П (экс 550п, 639п) не найдено!
            </span>
          </div>
        )}
      </div>

      {/* Цена и кнопка */}
      <div className="mt-auto">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg font-bold text-foreground">
              Стоимость {formatPrice(company.price)}
            </p>
          </div>
        </div>
        {onQuickView ? (
          <div className="flex gap-1.5 mt-3">
            <Button
              onClick={() => onQuickView(company)}
              variant="outline"
              size="sm"
              className="flex-1 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white text-xs sm:text-sm px-1.5 sm:px-2 py-1.5 min-w-0 max-w-none"
            >
              <span className="truncate">{t('quickView')}</span>
            </Button>
            <Button
              onClick={() => onDetailsClick(company)}
              size="sm"
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-xs sm:text-sm px-1.5 sm:px-2 py-1.5 min-w-0 max-w-none"
            >
              <span className="truncate">{t('details')}</span>
            </Button>
          </div>
        ) : (
          <Button
            onClick={() => onDetailsClick(company)}
            className="w-full mt-3 bg-orange-500 hover:bg-orange-600"
          >
            {t('details')}
          </Button>
        )}
      </div>
    </motion.div>
  )
}
